import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { FeatureCollection } from 'geojson';

// Mapbox token - should be in env, using public token for now
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || 'pk.eyJ1IjoiZXRpZ28iLCJhIjoiY2x4NHBqcG54MDJ6czJqcXo4a2g4d3VhZCJ9.placeholder';

interface RoutePreviewModalProps {
  geojson: FeatureCollection;
  title: string;
  onClose: () => void;
}

/** Modal to display route geometry on Mapbox map + GeoJSON JSON side-by-side */
export function RoutePreviewModal({ geojson, title, onClose }: RoutePreviewModalProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    mapboxgl.accessToken = 'pk.eyJ1IjoibWluaG5nb2MyNzA0OTkiLCJhIjoiY21tc3VkNTBlMWh5dTJxcHp3ZndtbWRoZCJ9.Vq80AybnFRngwQ_Kw8WbZg';
    const map = new mapboxgl.Map({
      container: mapRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [121.0, 14.5],
      zoom: 7,
    });

    map.on('load', () => {
      // Add GeoJSON source
      map.addSource('route', { type: 'geojson', data: geojson });
      // Add line layer
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        paint: { 'line-color': '#3b82f6', 'line-width': 4, 'line-opacity': 0.8 },
      });
      // Fit bounds
      if (geojson.features[0]?.geometry?.type === 'LineString') {
        const coords = (geojson.features[0].geometry as { coordinates: [number, number][] }).coordinates;
        if (coords.length >= 2) {
          const bounds = coords.reduce((b, c) => b.extend(c as [number, number]), new mapboxgl.LngLatBounds(coords[0], coords[0]));
          map.fitBounds(bounds, { padding: 40 });
        }
      }
    });

    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [geojson]);

  const handleCopy = () => navigator.clipboard.writeText(JSON.stringify(geojson, null, 2));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-6xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl cursor-pointer">&times;</button>
        </div>
        <div className="flex-1 flex overflow-hidden">
          <div ref={mapRef} className="w-1/2 h-full" />
          <div className="w-1/2 h-full flex flex-col border-l">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
              <span className="text-xs font-medium text-gray-600">GeoJSON</span>
              <button onClick={handleCopy} className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded cursor-pointer">Copy JSON</button>
            </div>
            <pre className="flex-1 overflow-auto p-3 text-xs font-mono bg-gray-50">{JSON.stringify(geojson, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}
